/**
 * Gateway Store - Manages Gateway connection and real-time state
 */

// Local type definitions for browser environment
export interface ServerStatus {
	startTime: Date;
	sessions: number;
	connections: number;
	port: number;
}

export interface Session {
	key: string;
	state: "idle" | "active" | "error";
	channel: string;
	agentId: string;
	createdAt: Date;
	lastActivity: Date;
	messageCount: number;
}

export interface GatewayState {
	connected: boolean;
	status: ServerStatus | null;
	sessions: Session[];
	channels: Array<{
		type: string;
		name: string;
		enabled: boolean;
		connected: boolean;
	}>;
	logs: string[];
}

export class GatewayStore {
	private ws?: WebSocket;
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 5;
	private reconnectTimeout?: ReturnType<typeof setTimeout>;
	private listeners = new Set<(state: GatewayState) => void>();

	state: GatewayState = {
		connected: false,
		status: null,
		sessions: [],
		channels: [],
		logs: [],
	};

	private notify() {
		for (const listener of this.listeners) {
			listener(this.state);
		}
	}

	subscribe(listener: (state: GatewayState) => void): () => void {
		this.listeners.add(listener);
		listener(this.state);
		return () => this.listeners.delete(listener);
	}

	async connect(url: string = "ws://localhost:18789/ws"): Promise<void> {
		return new Promise((resolve, reject) => {
			try {
				this.ws = new WebSocket(url + "?type=dashboard");

				this.ws.onopen = () => {
					console.log("Connected to Gateway");
					this.state.connected = true;
					this.reconnectAttempts = 0;
					this.notify();
					resolve();
				};

				this.ws.onmessage = (event) => {
					try {
						const data = JSON.parse(event.data);
						this.handleMessage(data);
					} catch (error) {
						console.error("Failed to parse message:", error);
					}
				};

				this.ws.onclose = () => {
					console.log("Disconnected from Gateway");
					this.state.connected = false;
					this.notify();
					this.attemptReconnect(url);
				};

				this.ws.onerror = (error) => {
					console.error("Gateway WebSocket error:", error);
					reject(error);
				};
			} catch (error) {
				reject(error);
			}
		});
	}

	disconnect(): void {
		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
		}
		this.ws?.close();
	}

	private attemptReconnect(url: string): void {
		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.error("Max reconnection attempts reached");
			return;
		}

		this.reconnectAttempts++;
		const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

		console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

		this.reconnectTimeout = setTimeout(() => {
			this.connect(url).catch(() => {
				// Error handled elsewhere
			});
		}, delay);
	}

	private handleMessage(data: unknown): void {
		if (typeof data !== "object" || data === null) return;

		const message = data as Record<string, unknown>;

		switch (message.type) {
			case "status":
				this.state.status = message.data as ServerStatus;
				this.notify();
				break;

			case "sessions":
				this.state.sessions = message.data as Session[];
				this.notify();
				break;

			case "channels":
				this.state.channels = message.data as GatewayState["channels"];
				this.notify();
				break;

			case "log":
				this.state.logs.unshift(message.data as string);
				if (this.state.logs.length > 100) {
					this.state.logs.pop();
				}
				this.notify();
				break;

			case "session:update":
				this.updateSession(message.data as Session);
				break;

			case "session:close": {
				const key = (message.data as { sessionKey: string }).sessionKey;
				this.state.sessions = this.state.sessions.filter((s) => s.key !== key);
				this.notify();
				break;
			}
		}
	}

	private updateSession(session: Session): void {
		const index = this.state.sessions.findIndex((s) => s.key === session.key);
		if (index >= 0) {
			this.state.sessions[index] = session;
		} else {
			this.state.sessions.push(session);
		}
		this.notify();
	}

	// Actions
	async startGateway(): Promise<void> {
		this.send({ type: "action", action: "start" });
	}

	async stopGateway(): Promise<void> {
		this.send({ type: "action", action: "stop" });
	}

	async restartChannel(channelType: string): Promise<void> {
		this.send({ type: "action", action: "restartChannel", channel: channelType });
	}

	private send(data: unknown): void {
		if (this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(data));
		}
	}
}

// Global store instance
export const gatewayStore = new GatewayStore();
